// Integrytas Group - Common Security Module (CSM)
// Personalization context definition + consumer hook.
// The provider (JSX) lives in components/personalization/PersonalizationProvider.tsx

import { createContext, useContext } from 'react';
import type { AuthSession } from '../types/auth';
import type { FieldPreference, ColumnPreference } from '../types/personalization';
import type { UldTier, UldRoleOwner } from '../hooks/use_uld_layout';
import type { UldDeltaAction } from './uld_merge';
import type { FieldLineage, InspectSelection } from '../types/fln';

export interface PersonalizationCtx {
  pageCode:            string;
  session:             AuthSession;
  isPersonalizing:     boolean;
  togglePersonalizing: () => void;
  /** Exit personalize mode unconditionally (e.g. when switching tabs). */
  stopPersonalizing:   () => void;

  /**
   * Field-lineage "Inspect" mode (FLN): when on, the page is informational — every
   * PersonalizedField / grid column is a click target that reveals where its data
   * comes from. Mutually exclusive with personalize mode.
   */
  isInspecting:         boolean;
  toggleInspecting:     () => void;
  /** Select a field to show its provenance (called on click while inspecting). */
  inspectField:         (componentKey: string, fieldKey: string, label: string) => void;
  inspectSelection:     InspectSelection | null;
  clearInspectSelection: () => void;
  /** Resolved provenance for a field on this page, or undefined if not documented. */
  lineageFor:           (componentKey: string, fieldKey: string) => FieldLineage | undefined;
  inspectLoading:       boolean;

  fieldPref:    (componentKey: string, fieldKey: string)                            => FieldPreference;
  setFieldPref: (componentKey: string, fieldKey: string, patch: Partial<FieldPreference>) => void;

  colPrefs:    (componentKey: string, defaultKeys: string[]) => Record<string, ColumnPreference>;
  setColPrefs: (componentKey: string, cols: Record<string, Partial<ColumnPreference>>)   => void;

  /**
   * The component key of an active FieldRenderer design panel, or null. When set,
   * that panel is the single editor for its fields, so PersonalizedField renders
   * read-only (no inline editor) and reads its prefs from this key.
   */
  designPanelKey:      string | null;
  registerDesignPanel: (key: string | null) => void;

  /** The tier (System / Role / User) personalization edits are saved to. */
  availableTiers: UldTier[];
  tiersLoading:   boolean;
  editTier:       UldTier;
  setEditTier:    (t: UldTier) => void;
  roleOwner:      UldRoleOwner | null;
  /** The merged delta for a component at the current edit tier (below + own draft). */
  componentDelta: (componentKey: string) => UldDeltaAction[];

  /** Last save error (e.g. a Role design owned by another role); shown to the user. */
  lastError:  string | null;
  clearError: () => void;

  /** Delete a component's personalization at the edit tier (true reset, not defaults). */
  resetComponent: (componentKey: string) => Promise<void>;
  /** Bumps on each reset so column managers re-seed from the cleared state. */
  resetTick: number;

  /**
   * "Show original" preview: while true, the page renders as though the current
   * edit tier carried no personalization (only the tiers BELOW it apply). The
   * user's in-progress draft is preserved — exiting preview restores it. Edit
   * affordances are suppressed and prefs become read-only for the duration.
   */
  previewBaseline: boolean;
  startPreview:    () => void;
  exitPreview:     () => void;

  flush: () => Promise<void>;
}

export const PersonalizationContext = createContext<PersonalizationCtx | null>(null);

export function usePersonalization(): PersonalizationCtx {
  const ctx = useContext(PersonalizationContext);
  if (!ctx) throw new Error('usePersonalization must be used inside PersonalizationProvider');
  return ctx;
}

/** Context accessor that returns null outside a provider (for shared components
 *  like FieldRenderer that may render with or without personalization). */
export function usePersonalizationOptional(): PersonalizationCtx | null {
  return useContext(PersonalizationContext);
}
